import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { formatJalali, formatMoney, formatNumber } from "../lib/format";
import { ReceiptDialog, type ReceiptTarget } from "../components/ReceiptDialog";
import { ContractDialog } from "../components/ContractDialog";
import { PrintableReceipt } from "../components/PrintableReceipt";
import { useT } from "../i18n";
import type { StringKey } from "../i18n/fa";

interface BookingRow {
  id: string;
  date: string;
  shift: string;
  status: string;
  guestCount: number;
  paidMinor: number;
  balanceMinor: number;
  contractId: string | null;
  quote: { totalMinor: number; currency: string; depositDueMinor: number };
}

const TABS: { value: string; labelKey: StringKey }[] = [
  { value: "pending_request", labelKey: "booking_tab_requests" },
  { value: "pending_deposit", labelKey: "booking_tab_pending_deposit" },
  { value: "confirmed", labelKey: "booking_tab_confirmed" },
  { value: "completed", labelKey: "booking_tab_completed" },
];

const SHIFT_KEY: Record<string, StringKey> = {
  MORNING: "shift_morning",
  EVENING: "shift_evening",
  FULL_DAY: "shift_full_day",
};

export function BookingsPage() {
  const { orgId } = useSession();
  const t = useT();
  const [tab, setTab] = useState<string>("pending_request");
  const [rows, setRows] = useState<BookingRow[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [receiptTarget, setReceiptTarget] = useState<ReceiptTarget | null>(null);
  const [contractView, setContractView] = useState<string | null>(null);
  const [printPaymentId, setPrintPaymentId] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) return;
    const q = query(
      collection(db, "bookings"),
      where("orgId", "==", orgId),
      where("status", "==", tab),
      orderBy("date"),
    );
    return onSnapshot(q, (snap) => {
      setRows(
        snap.docs.map((d) => {
          const data = d.data() as Omit<BookingRow, "id">;
          return { id: d.id, ...data, contractId: data.contractId ?? null };
        }),
      );
    });
  }, [orgId, tab]);

  async function confirmRequest(bookingId: string) {
    setBusyId(bookingId);
    try {
      await api(`/orgs/${orgId}/bookings/${bookingId}/confirm`, { method: "POST" });
      setToast(t("bookings_toast_confirmed"));
    } catch (e) {
      setToast(e instanceof Error ? e.message : t("common_error"));
    } finally {
      setBusyId(null);
    }
  }

  async function issueContract(bookingId: string) {
    setBusyId(bookingId);
    try {
      const result = await api<{ contractId: string }>(
        `/orgs/${orgId}/bookings/${bookingId}/contract`,
        { method: "POST" },
      );
      setToast(t("bookings_toast_contract_issued"));
      setContractView(result.contractId);
    } catch (e) {
      setToast(e instanceof Error ? e.message : t("common_error"));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <Typography variant="h5" mb={2}>
        {t("bookings_title")}
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {TABS.map((item) => (
          <Tab key={item.value} value={item.value} label={t(item.labelKey)} />
        ))}
      </Tabs>

      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("bookings_col_date")}</TableCell>
              <TableCell>{t("bookings_col_shift")}</TableCell>
              <TableCell>{t("bookings_col_guests")}</TableCell>
              <TableCell>{t("bookings_col_total")}</TableCell>
              <TableCell>{t("bookings_col_paid")}</TableCell>
              <TableCell>{t("bookings_col_balance")}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{formatJalali(row.date)}</TableCell>
                <TableCell>
                  <Chip size="small" label={SHIFT_KEY[row.shift] ? t(SHIFT_KEY[row.shift]) : row.shift} />
                </TableCell>
                <TableCell>{formatNumber(row.guestCount)}</TableCell>
                <TableCell>{formatMoney(row.quote.totalMinor, row.quote.currency)}</TableCell>
                <TableCell>{formatMoney(row.paidMinor, row.quote.currency)}</TableCell>
                <TableCell>{formatMoney(row.balanceMinor, row.quote.currency)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    {row.status === "pending_request" && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={busyId === row.id}
                        onClick={() => confirmRequest(row.id)}
                      >
                        {t("bookings_confirm")}
                      </Button>
                    )}
                    {["pending_deposit", "confirmed"].includes(row.status) && (
                      <>
                        {row.balanceMinor > 0 && (
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={() =>
                              setReceiptTarget({
                                bookingId: row.id,
                                balanceMinor: row.balanceMinor,
                                depositDueMinor: row.quote.depositDueMinor,
                                paidMinor: row.paidMinor,
                              })
                            }
                          >
                            {t("receipt_submit")}
                          </Button>
                        )}
                        {row.contractId ? (
                          <Button size="small" onClick={() => setContractView(row.contractId)}>
                            {t("bookings_contract")}
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            disabled={busyId === row.id}
                            onClick={() => issueContract(row.id)}
                          >
                            {t("bookings_issue_contract")}
                          </Button>
                        )}
                      </>
                    )}
                    {row.status === "completed" && row.contractId && (
                      <Button size="small" onClick={() => setContractView(row.contractId)}>
                        {t("bookings_contract")}
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" textAlign="center" py={3}>
                    {t("common_empty")}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {orgId && (
        <ReceiptDialog
          orgId={orgId}
          target={receiptTarget}
          onClose={() => setReceiptTarget(null)}
          onRecorded={({ receiptNo, paymentId }) => {
            setToast(t("bookings_toast_receipt", { no: receiptNo }));
            setPrintPaymentId(paymentId);
          }}
        />
      )}
      <ContractDialog contractId={contractView} onClose={() => setContractView(null)} />
      <PrintableReceipt paymentId={printPaymentId} onClose={() => setPrintPaymentId(null)} />

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert onClose={() => setToast(null)} closeText={t("common_close")} severity="info">
          {toast}
        </Alert>
      </Snackbar>
    </>
  );
}
